import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Filter,
  Pause,
  Play,
  Search,
  User,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { ElevenLabsVoice } from '@/api/aiAgents'

interface Props {
  isOpen: boolean
  onClose: () => void
  voices: ElevenLabsVoice[]
  selectedVoiceId: string
  onSelectVoice: (voiceId: string) => void
}

/** Port 1:1 du Next VoiceSelectionModal — sélection voix ElevenLabs avec preview audio. */
export function VoiceSelectionModal({
  isOpen,
  onClose,
  voices,
  selectedVoiceId,
  onSelectVoice,
}: Props) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
  const [tempSelectedVoiceId, setTempSelectedVoiceId] = useState(selectedVoiceId)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Filtres : panneau dropdown au clic sur le bouton Filters
  const [showFilters, setShowFilters] = useState(false)
  const [filterGender, setFilterGender] = useState<'all' | 'male' | 'female'>('all')
  const [filterAccent, setFilterAccent] = useState<string>('all')
  const filtersRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) setTempSelectedVoiceId(selectedVoiceId)
  }, [isOpen, selectedVoiceId])

  // Reset des filtres à la fermeture du modal pour éviter un état stale.
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setFilterGender('all')
      setFilterAccent('all')
      setShowFilters(false)
    }
  }, [isOpen])

  // Ferme le dropdown filtres au clic extérieur.
  useEffect(() => {
    if (!showFilters) return
    const handler = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setShowFilters(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showFilters])

  // Liste dynamique des accents présents dans les voix dispo pour peupler le dropdown.
  const availableAccents = useMemo(() => {
    const set = new Set<string>()
    voices.forEach((v) => {
      if (v.labels?.accent) set.add(v.labels.accent)
    })
    return Array.from(set).sort()
  }, [voices])

  const activeFilterCount =
    (filterGender !== 'all' ? 1 : 0) + (filterAccent !== 'all' ? 1 : 0)

  const handlePlayPause = (e: React.MouseEvent, voice: ElevenLabsVoice) => {
    e.stopPropagation()
    if (!voice.preview_url) return
    if (playingVoiceId === voice.voice_id && audioRef.current) {
      audioRef.current.pause()
      setPlayingVoiceId(null)
      return
    }
    if (audioRef.current) audioRef.current.pause()
    const audio = new Audio(voice.preview_url)
    void audio.play()
    setPlayingVoiceId(voice.voice_id)
    audioRef.current = audio
    audio.onended = () => setPlayingVoiceId(null)
  }

  const formatLabel = (key: 'gender' | 'accent' | 'age', value: string) => {
    if (key === 'gender') {
      return value === 'male'
        ? t('manager.aiAgents.voiceModal.filters.male')
        : value === 'female'
          ? t('manager.aiAgents.voiceModal.filters.female')
          : value.charAt(0).toUpperCase() + value.slice(1)
    }
    // Pour accent et age : capitalisation simple, gestion des tirets ("n-african" → "N-African").
    return value
      .split(/[-\s]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(value.includes('-') ? '-' : ' ')
  }

  const filteredVoices = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return voices.filter((voice) => {
      const matchSearch = q === '' || voice.name.toLowerCase().includes(q)
      const matchGender =
        filterGender === 'all' || voice.labels?.gender === filterGender
      const matchAccent =
        filterAccent === 'all' || voice.labels?.accent === filterAccent
      return matchSearch && matchGender && matchAccent
    })
  }, [voices, searchQuery, filterGender, filterAccent])

  const handleConfirm = () => {
    onSelectVoice(tempSelectedVoiceId)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[680px] lg:max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            {t('manager.aiAgents.voiceModal.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder={t('manager.aiAgents.voiceModal.searchPlaceholder')}
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="relative" ref={filtersRef}>
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => setShowFilters((v) => !v)}
              >
                <Filter className="h-4 w-4" />
                {t('manager.aiAgents.filters')}
                {activeFilterCount > 0 && (
                  <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-600 text-white text-xs font-semibold px-1.5">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {showFilters && (
                <div className="absolute right-0 top-full mt-2 z-30 w-72 rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 shadow-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">
                      {t('manager.aiAgents.voiceModal.filters.title')}
                    </span>
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setFilterGender('all')
                          setFilterAccent('all')
                        }}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {t('manager.aiAgents.voiceModal.filters.reset')}
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-slate-400 block mb-1.5">
                      {t('manager.aiAgents.voiceModal.gender')}
                    </label>
                    <select
                      value={filterGender}
                      onChange={(e) =>
                        setFilterGender(e.target.value as 'all' | 'male' | 'female')
                      }
                      className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">
                        {t('manager.aiAgents.voiceModal.filters.all')}
                      </option>
                      <option value="male">
                        {t('manager.aiAgents.voiceModal.filters.male')}
                      </option>
                      <option value="female">
                        {t('manager.aiAgents.voiceModal.filters.female')}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-slate-400 block mb-1.5">
                      {t('manager.aiAgents.voiceModal.accent')}
                    </label>
                    <select
                      value={filterAccent}
                      onChange={(e) => setFilterAccent(e.target.value)}
                      className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={availableAccents.length === 0}
                    >
                      <option value="all">
                        {t('manager.aiAgents.voiceModal.filters.all')}
                      </option>
                      {availableAccents.map((acc) => (
                        <option key={acc} value={acc}>
                          {formatLabel('accent', acc)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {filterGender !== 'all' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2.5 py-1">
                  {t('manager.aiAgents.voiceModal.gender')}:{' '}
                  {t(`manager.aiAgents.voiceModal.filters.${filterGender}`)}
                  <button
                    type="button"
                    onClick={() => setFilterGender('all')}
                    className="hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filterAccent !== 'all' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2.5 py-1">
                  {t('manager.aiAgents.voiceModal.accent')}: {formatLabel('accent', filterAccent)}
                  <button
                    type="button"
                    onClick={() => setFilterAccent('all')}
                    className="hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="border rounded-md flex-1 overflow-auto">
            <Table>
              <TableHeader className="bg-gray-50 sticky top-0 z-10">
                <TableRow>
                  <TableHead className="w-[50px]" />
                  <TableHead className="min-w-[180px]">
                    {t('manager.aiAgents.voiceModal.voiceName')}
                  </TableHead>
                  <TableHead className="w-[120px] hidden sm:table-cell">
                    {t('manager.aiAgents.voiceModal.accent')}
                  </TableHead>
                  <TableHead className="w-[100px] hidden sm:table-cell">
                    {t('manager.aiAgents.voiceModal.gender')}
                  </TableHead>
                  <TableHead className="text-right w-[80px]">
                    {t('manager.aiAgents.voiceModal.action')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-gray-500">
                      {t('manager.aiAgents.voiceModal.noVoicesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVoices.map((voice) => {
                    const isSelected = tempSelectedVoiceId === voice.voice_id
                    const isPlaying = playingVoiceId === voice.voice_id
                    return (
                      <TableRow
                        key={voice.voice_id}
                        className={cn(
                          'cursor-pointer hover:bg-blue-50/50 transition-colors',
                          isSelected && 'bg-blue-50 hover:bg-blue-50',
                        )}
                        onClick={() => setTempSelectedVoiceId(voice.voice_id)}
                      >
                        <TableCell className="w-[50px]">
                          <div
                            className={cn(
                              'h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors',
                              isSelected
                                ? 'border-blue-600 bg-blue-600'
                                : 'border-gray-300 hover:border-blue-400',
                            )}
                          >
                            {isSelected && (
                              <div className="h-1.5 w-1.5 rounded-full bg-white" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="min-w-0">
                          <div className="flex items-center gap-3 min-w-0">
                            <Avatar className="h-8 w-8 flex-shrink-0">
                              <AvatarImage
                                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${voice.name}`}
                              />
                              <AvatarFallback>
                                <User className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                            <span
                              className="font-medium text-gray-900 truncate"
                              title={voice.name}
                            >
                              {voice.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600 whitespace-nowrap hidden sm:table-cell">
                          {voice.labels?.accent
                            ? formatLabel('accent', voice.labels.accent)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-gray-600 whitespace-nowrap hidden sm:table-cell">
                          {voice.labels?.gender
                            ? formatLabel('gender', voice.labels.gender)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 rounded-full hover:bg-gray-200"
                            onClick={(e) => handlePlayPause(e, voice)}
                          >
                            {isPlaying ? (
                              <Pause className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Play className="h-4 w-4 text-gray-500" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter className="p-6 pt-2 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            {t('manager.aiAgents.voiceModal.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {t('manager.aiAgents.voiceModal.addVoice')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
